// AdminSchoolsPage.jsx — system‑admin landing screen (AntD/Redux)
// -----------------------------------------------------------------------------
// ▸ Lists every school in a searchable Ant Design <Table>
// ▸ Clicking a row routes to “/sysadmin/schools/:id”
// ▸ Mirrors the UsersPage pattern (search + table)

import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, connect } from "react-redux";
import { Table, Input, Row, Col, Button } from "antd";
import { getSchools } from "../../../_actions/user_actions"; // 🔧 create this action

function AdminSchoolsPage(props) {
  const dispatch = useDispatch();

  // ─── Redux‑state selectors ───────────────────────────────────────────────────
  const schoolsData = useMemo(() => {
      return props.user?.schoolsData ?? [];
  }, [props.user?.schoolsData])
  const isLoading = !props.user?.schoolsData; // crude loading flag

  // ─── Local state ─────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");

  // ─── Derived (memoised) data ─────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    const filter = String(search).toLowerCase();
    if (!filter) return schoolsData;

    return schoolsData.filter((school) => {
      const name = school.name?.toLowerCase() || "";
      const city = school.city?.toLowerCase() || "";
      const zip = String(school.zip ?? "")
      return name.includes(filter) || zip.includes(filter) || city.includes(filter);
    });
  }, [schoolsData, search]);

  // ─── Column definitions (AntD) ───────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "# Staff",
      dataIndex: "staff",
      key: "staff",
      align: "center",
      sorter: (a, b) => a.staff - b.staff,
    },
    {
      title: "# Students",
      dataIndex: "students",
      key: "students",
      align: "center",
      sorter: (a, b) => a.students - b.students,
    },
    {
      title: "ZIP Code",
      dataIndex: "zip",
      key: "zip",
      align: "center",
      sorter: (a, b) => a.zip - b.zip,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      sorter: (a, b) => a.city.localeCompare(b.city),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      align: "center",
      render: (text, record) => (
        <Button
          type="primary"
          onClick={(e) => {
            e.stopPropagation();
            props.history.push("/sysadmin/message/" + record._id)
          }}
        >
          Message
        </Button>
      ),
    },
    {
      title: "Paused",
      dataIndex: "isPaused",
      key: "isPaused",
      align: "center",
      render: (value) => (value ? 
        <span style={{ color: "red" }}>Paused</span> : 
        <span style={{ color: "green" }}>Active</span>
      ),
      sorter: (a, b) => Number(b.isPaused) - Number(a.isPaused),
    },
  ];

  // ─── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(getSchools());
  }, [dispatch]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const onRowClick = (record) => ({
    onClick: () => props.history.push("/sysadmin/schools/" + record._id),
  });

  // ─── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className="spread">
      <Row gutter={16} className="admin‑toolbar">
        <Col span={8}>
          <Input
            autoFocus
            placeholder="Search by name, city or ZIP"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col span={4} className="text‑center">
          <Button
            type="primary"
            onClick={() => props.history.push("/sysadmin/create")}
          >
            Create School
          </Button>
        </Col>

        <Col span={4} className="text‑center">
          <Button
            type="primary"
            onClick={() => props.history.push("/sysadmin/message/all")}
          >
            Message All
          </Button>
        </Col>
      </Row>

      <br />

      <Table
        rowKey="_id"
        dataSource={filteredData}
        columns={columns}
        onRow={onRowClick}
        pagination={{ pageSize: 20 }}
        loading={isLoading}
        className="admin‑table"
      />
    </div>
  );
}

function mapStateToProps(state) {
  return { data: state };
}

export default withRouter(connect(mapStateToProps)(AdminSchoolsPage));
